import React, { useState, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Mail, Phone, User, Building, FileText, Calculator, Package, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { sendQuoteEmailViaBackend, validateEmail, QuoteFormData } from '../services/backendEmailService';
import { clientService } from '../services/clientService';
import { quoteService } from '../services/quoteService';
import { notificationService } from '../services/notificationService';

// Variable global para prevenir duplicados
let lastSubmitTime = 0;
const SUBMIT_COOLDOWN = 5000; // 5 segundos (aumentado)

const Quote = () => {
  const isSubmittingRef = useRef(false);
  const [formData, setFormData] = useState<QuoteFormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    ruc: '',
    position: '',
    industry: '',
    service: '',
    productType: '',
    specifications: '',
    urgency: 'normal',
    message: ''
  });
  
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const now = Date.now();
    
    // Prevent double submission - multiple checks con useRef
    if (isSubmittingRef.current || isSubmitting) {
      console.log('🚫 Ya se está enviando, ignorando clic duplicado');
      return;
    }
    
    // Cooldown check
    if (now - lastSubmitTime < SUBMIT_COOLDOWN) {
      console.log('🚫 En cooldown, ignorando clic rápido');
      notificationService.warning('Espere un momento', 'Por favor, espera 5 segundos antes de enviar otra solicitud.');
      return;
    }
    
    // Reset status
    setSubmitStatus('idle');
    
    // Validate form
    if (!formData.name || !formData.email || !formData.phone) {
      notificationService.warning('Campos incompletos', 'Por favor, complete los campos obligatorios: Nombre, Email y Teléfono.');
      return;
    }
    
    // Validate email format
    if (!validateEmail(formData.email)) {
      notificationService.warning('Email inválido', 'Por favor, ingrese un email válido.');
      return;
    }
    
    // Update last submit time IMMEDIATELY y refs
    lastSubmitTime = now;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    
    console.log('🚀 Iniciando envío de formulario');
    
    try {
      // Send email using backend
      const emailSuccess = await sendQuoteEmailViaBackend(formData);
      
      // Add or find customer in database
      const customerData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        ruc: formData.ruc,
        industry: formData.industry,
        notes: `Solicitud de cotización - ${formData.service ? `Servicio: ${formData.service}` : 'Sin servicio especificado'} - ${formData.productType ? `Producto: ${formData.productType}` : 'Sin producto especificado'} - Cargo: ${formData.position || 'No especificado'}`
      };
      
      let customer = null;
      let customerSuccess = false;
      
      try {
        customer = await clientService.findOrCreateCustomer(customerData);
        customerSuccess = !!customer;
        console.log('Cliente procesado:', customer);
      } catch (error) {
        console.error('Error al procesar cliente:', error);
      }
      
      // Create pending quote without products
      const quoteData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: `${formData.service ? `Servicio de interés: ${formData.service}\n\n` : ''}${formData.industry ? `Industria: ${formData.industry}\n\n` : ''}${formData.productType ? `Tipo de producto: ${formData.productType}\n\n` : ''}${formData.ruc ? `RUC: ${formData.ruc}\n\n` : ''}${formData.specifications ? `Especificaciones: ${formData.specifications}\n\n` : ''}${formData.urgency ? `Urgencia: ${formData.urgency}\n\n` : ''}${formData.message}`,
        product_name: formData.service || formData.productType || 'Solicitud de cotización general'
      };
      
      const quoteSuccess = await quoteService.createPendingQuote(quoteData, customer?.id);
      
      if (emailSuccess || customerSuccess || quoteSuccess) {
        setSubmitStatus('success');
        // Reset form on success
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          ruc: '',
          position: '',
          industry: '',
          service: '',
          productType: '',
          specifications: '',
          urgency: 'normal',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error al enviar el formulario:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  const industries = [
    'Industria Papelera',
    'Industria Pesquera',
    'Industria Alimentaria',
    'Agroindustria',
    'Industria Manufacturer',
    'Otra'
  ];

  const services = [
    'Mantenimiento Industrial',
    'Instalación y Montaje',
    'Consultoría Técnica',
    'Repuestos y Accesorios',
    'Inspección y Certificación',
    'Capacitación Técnica'
  ];


  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-2 bg-accent-100 text-accent-700 rounded-full text-sm font-medium mb-6">
                <Calculator className="w-4 h-4 mr-2" />
                Solicitar Cotización
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Obtén tu
                <span className="text-gradient"> Cotización Personalizada</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Completa el formulario below y recibe una cotización detallada para tus necesidades 
                industriales. Nuestro equipo te contactará a la brevedad.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-12">
              {/* Quote Form */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <FileText className="w-6 h-6 mr-3 text-primary-600" />
                    Información de Contacto
                  </h2>
                   
                  <form onSubmit={handleSubmit} className="space-y-6" method="post">
                    {/* Primera fila: Nombre Completo, Cargo */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nombre Completo *
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="Tu nombre completo"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Cargo
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="text"
                            name="position"
                            value={formData.position}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="Tu cargo en la empresa"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Segunda fila: Email, Teléfono */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email *
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="tu@email.com"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Teléfono *
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            required
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="+51 953 759 419"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Tercera fila: Empresa, RUC */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Empresa
                        </label>
                        <div className="relative">
                          <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                          <input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="Nombre de tu empresa"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          RUC
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            name="ruc"
                            value={formData.ruc}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="Número de RUC de tu empresa"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Cuarta fila: Industria, Servicio de Interés */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Industria
                        </label>
                        <select
                          name="industry"
                          value={formData.industry}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="">Selecciona tu industria</option>
                          {industries.map((industry, index) => (
                            <option key={index} value={industry}>{industry}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Servicio de Interés
                        </label>
                        <select
                          name="service"
                          value={formData.service}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="">Selecciona un servicio</option>
                          {services.map((service, index) => (
                            <option key={index} value={service}>{service}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Quinta fila: Nivel de Urgencia */}
                    <div className="grid md:grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nivel de Urgencia
                        </label>
                        <div className="flex flex-row space-x-6">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="urgency"
                              value="normal"
                              checked={formData.urgency === 'normal'}
                              onChange={handleInputChange}
                              className="mr-2"
                            />
                            <span className="text-gray-700">Normal (3-5 días hábiles)</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="urgency"
                              value="urgente"
                              checked={formData.urgency === 'urgente'}
                              onChange={handleInputChange}
                              className="mr-2"
                            />
                            <span className="text-gray-700">Urgente (24-48 horas)</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Sexta fila: Especificaciones Técnicas */}
                    <div className="grid md:grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Especificaciones Técnicas
                        </label>
                        <textarea
                          name="specifications"
                          value={formData.specifications}
                          onChange={handleInputChange}
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                          placeholder="Describe las especificaciones técnicas, dimensiones, materiales requeridos, etc."
                        ></textarea>
                      </div>
                    </div>

                    {/* Séptima fila: Mensaje Adicional */}
                    <div className="grid md:grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Mensaje Adicional
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                          placeholder="Información adicional que consideres relevante..."
                        ></textarea>
                      </div>
                    </div>

                    {/* Botón de envío */}
                    <div className="md:col-span-1">
                      <Button
                        type="submit"
                        className="w-full btn-primary text-lg py-4"
                        disabled={isSubmitting}
                        onClick={(e) => {
                          // Prevenir propagación del evento
                          if (isSubmitting) {
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                          }
                        }}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Package className="w-5 h-5 mr-2" />
                            Enviar Solicitud de Cotización
                          </>
                        )}
                      </Button>
                    </div>
                   
                    {/* Mensajes de estado */}
                    {submitStatus === 'success' && (
                      <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-800 text-center">
                          ¡Solicitud de cotización enviada con éxito! Nos pondremos en contacto contigo a la brevedad.
                        </p>
                      </div>
                    )}
                   
                    {submitStatus === 'error' && (
                      <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-800 text-center">
                          Hubo un error al enviar tu solicitud. Por favor, inténtalo nuevamente más tarde.
                        </p>
                      </div>
                    )}
                  </form>
                </div>
              </div>

              {/* Information Cards - Right Sidebar */}
              <div className="lg:col-span-1 space-y-8">
                <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2 text-primary-600" />
                    ¿Por qué elegirnos?
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-base font-semibold text-gray-900 mb-2">Respuesta Rápida</h4>
                      <p className="text-gray-600 text-sm">
                        Recibirás una respuesta en menos de 24 horas con precios competitivos.
                      </p>
                    </div>
                    <div>
                      <h4 className="text-base font-semibold text-gray-900 mb-2">Expertos en la Industria</h4>
                      <p className="text-gray-600 text-sm">
                        Más de 15 años de experiencia en las principales industrias del país.
                      </p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-7 h-7 bg-secondary-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">1</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">Solicitud Recibida</h4>
                        <p className="text-gray-600 text-xs">
                          Procesamos tu solicitud inmediatamente.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-7 h-7 bg-secondary-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">2</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">Análisis Técnico</h4>
                        <p className="text-gray-600 text-xs">
                          Nuestros ingenieros preparan tu cotización.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-7 h-7 bg-secondary-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">3</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">Envío de Cotización</h4>
                        <p className="text-gray-600 text-xs">
                          Recibes tu cotización personalizada.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-secondary-50 rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <Clock className="w-5 h-5 mr-2 text-secondary-600" />
                    Proceso de Cotización
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-7 h-7 bg-secondary-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">1</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">Solicitud Recibida</h4>
                        <p className="text-gray-600 text-xs">
                          Procesamos tu solicitud inmediatamente.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-7 h-7 bg-secondary-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">2</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">Análisis Técnico</h4>
                        <p className="text-gray-600 text-xs">
                          Nuestros ingenieros preparan tu cotización.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-7 h-7 bg-secondary-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm">3</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">Envío de Cotización</h4>
                        <p className="text-gray-600 text-xs">
                          Recibes tu cotización personalizada.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Quote;